import { useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@nurion/ui/popover";

import { DndIcon } from "~/components/DndIcon";
import { DndScheduleDialog } from "~/components/DndScheduleDialog";
import { usePortalRoot } from "~/components/EmbedPortalContext";
import type { DndAccountData } from "~/lib/matrix";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

export function DndMenu({
  anchor,
  data,
  active,
  onOff,
  onManualUntil,
  onSchedule,
  onClose,
}: {
  anchor: HTMLElement | null;
  data: DndAccountData;
  active: boolean;
  onOff: () => void;
  onManualUntil: (until: Date | null) => void;
  onSchedule: (schedule: DndAccountData["schedule"]) => void;
  onClose: () => void;
}) {
  const portalRoot = usePortalRoot();
  const [customTime, setCustomTime] = useState("18:00");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const underline = useUnderline();

  if (!anchor || !portalRoot) return null;

  const collisionBoundary =
    typeof document !== "undefined" && portalRoot !== document.body ? portalRoot : undefined;

  const setUntil = (until: Date | null) => {
    onManualUntil(until);
    onClose();
  };
  const turnOff = () => {
    onOff();
    onClose();
  };

  return (
    <>
      <Popover
        open
        onOpenChange={(open) => {
          if (!open && !scheduleOpen) onClose();
        }}
      >
        <PopoverAnchor virtualRef={{ current: anchor }} />
        <PopoverContent
          portalContainer={portalRoot}
          side="top"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          collisionBoundary={collisionBoundary}
          aria-label="Do not disturb"
          className="z-[70] w-[260px] rounded-[var(--chat-radius-xl)] border border-[var(--chat-surface-border)] bg-[var(--chat-surface-overlay)] p-[var(--chat-space-2)] text-[var(--chat-fg-primary)] shadow-[var(--chat-shadow-popover)] backdrop-blur-none"
          style={{ fontFamily: "var(--chat-font-sans)" }}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            anchor.focus();
          }}
          onInteractOutside={(event) => {
            // The trigger lives outside the popover, so a click on it
            // dismisses here AND fires the trigger's own toggle, which
            // would immediately reopen the menu. Let the trigger own
            // that close.
            const target = event.detail.originalEvent.target;
            if (target instanceof Node && anchor.contains(target)) event.preventDefault();
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-2)",
              padding: "var(--chat-space-2)",
            }}
          >
            <DndIcon
              size={14}
              strokeWidth={ICON_STROKE}
              aria-hidden
              style={{ color: "var(--chat-fg-muted)" }}
            />
            <span style={{ fontSize: "var(--chat-type-body-sm-size)", fontWeight: 500 }}>
              Do not disturb
            </span>
          </div>
          <MenuButton selected={!active} onClick={turnOff}>
            Off
          </MenuButton>
          <MenuButton onClick={() => setUntil(addHours(1))}>For 1 hour</MenuButton>
          <MenuButton onClick={() => setUntil(addHours(4))}>For 4 hours</MenuButton>
          <MenuButton onClick={() => setUntil(endOfToday())}>Today</MenuButton>
          <MenuButton onClick={() => setUntil(tomorrowMorning())}>Tomorrow</MenuButton>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-2)",
              padding: "var(--chat-space-2)",
            }}
          >
            <span
              style={{
                color: "var(--chat-fg-secondary)",
                fontSize: "var(--chat-type-body-sm-size)",
              }}
            >
              Until
            </span>
            <input
              type="time"
              value={customTime}
              onChange={(event) => setCustomTime(event.currentTarget.value)}
              onFocus={underline.onFocus}
              onBlur={underline.onBlur}
              style={{
                minWidth: 0,
                flex: 1,
                border: "none",
                borderBottom: `1px solid ${underline.borderColor}`,
                background: "transparent",
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                outline: "none",
                padding: "0 0 var(--chat-space-1)",
                transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
              }}
            />
            <button
              type="button"
              className="chat-button"
              onClick={() => setUntil(nextTime(customTime))}
              style={{
                border: "none",
                borderRadius: "var(--chat-radius-sm)",
                background: "transparent",
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                fontWeight: 500,
                padding: "var(--chat-space-1) var(--chat-space-2)",
                cursor: "pointer",
              }}
            >
              Custom
            </button>
          </div>
          <MenuButton onClick={() => setScheduleOpen(true)}>Weekly schedule</MenuButton>
        </PopoverContent>
      </Popover>
      {scheduleOpen ? (
        <DndScheduleDialog
          schedule={data.schedule}
          onSave={onSchedule}
          onClose={() => {
            setScheduleOpen(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

function MenuButton({
  children,
  selected,
  onClick,
}: {
  children: string;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="chat-button"
      onClick={onClick}
      style={{
        width: "100%",
        minHeight: "var(--chat-space-8)",
        display: "flex",
        alignItems: "center",
        padding: "0 var(--chat-space-2)",
        borderRadius: "var(--chat-radius-sm)",
        background: selected ? "var(--chat-surface-panel)" : "transparent",
        color: "var(--chat-fg-primary)",
        fontSize: "var(--chat-type-body-md-size)",
        textAlign: "left",
      }}
    >
      {children}
    </button>
  );
}

function addHours(hours: number): Date {
  return new Date(Date.now() + hours * 60 * 60 * 1000);
}

function endOfToday(): Date {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date;
}

function tomorrowMorning(): Date {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return date;
}

function nextTime(value: string): Date {
  const [hourRaw, minuteRaw] = value.split(":");
  const date = new Date();
  date.setHours(Number(hourRaw ?? 0), Number(minuteRaw ?? 0), 0, 0);
  if (date.getTime() <= Date.now()) date.setDate(date.getDate() + 1);
  return date;
}
